"use client";

import { useBrand } from "@/Providers/Brand";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shadcnComponents/ui/avatar";
import { useToast } from "@/shadcnComponents/ui/use-toast";
import { Star } from "lucide-react";
import { useEffect, useState } from "react";

// Update the interface to include the onSubmit prop
interface InfluencerRatingProps {
  influencerId: string;
  collabId: string;
  name: string;
  image: string;
  initials: string;
  onSubmit?: () => void;
  isAlreadySubmitted?: boolean;
  rating?: number;
}

export function InfluencerRating({
  influencerId,
  collabId,
  name,
  image,
  initials,
  onSubmit,
  isAlreadySubmitted = false,
  rating = 0,
}: InfluencerRatingProps) {
  const { toast } = useToast();

  const [contentRating, setContentRating] = useState(0);
  const [collaborationLikelihood, setCollaborationLikelihood] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const brandState = useBrand();

  useEffect(() => {
    if (isAlreadySubmitted) {
      setIsSubmitted(true);
      setContentRating(rating);
    } else {
      setContentRating(0);
      setIsSubmitted(false);
      setCollaborationLikelihood(0);
      setFeedback("");
    }
  }, [isAlreadySubmitted, rating, influencerId]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    if (!contentRating || !collaborationLikelihood) {
      setIsSubmitting(false);
      return toast({
        title: "Please fill out all fields",
        variant: "destructive",
      });
    }
    const result = await brandState?.submitCollabRating({
      rating: contentRating,
      categories: {
        collaborationLikelihood,
      },
      review: feedback,
      collabId,
      influencerId,
    });
    if (result?.success) {
      toast({
        title: "Feedback submitted successfully",
        description: "Thank you for your feedback!",
        variant: "default",
      });
      setIsSubmitting(false);
      setIsSubmitted(true);
      setContentRating(0);
      setCollaborationLikelihood(0);
      setFeedback("");
    } else {
      setIsSubmitting(false);
      toast({
        title: result?.Error,
        variant: "destructive",
      });
    }
    if (onSubmit) {
      onSubmit();
    }
  };

  return (
    <div className="w-full">
      <div className="pb-2 px-4 pt-3 bg-gradient-to-r from-red-50 to-white">
        <div className="flex items-center gap-3">
          <Avatar className="h-12 w-12 border-2 border-red-300/30">
            <AvatarImage src={image || "/placeholder.svg"} alt={name} />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <h3 className="text-sm font-medium text-red-600">{name}</h3>
        </div>
      </div>

      {!isSubmitted ? (
        <div className="space-y-6 px-4 py-3">
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">
              How was your overall experience?
            </h3>
            <p className="text-xs text-gray-500">
              Consider content quality, revisions, and campaign effectiveness
            </p>
            <div className="flex gap-1.5 mt-1">
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  type="button"
                  onClick={() => setContentRating(rating)}
                  className="focus:outline-none transition-transform hover:scale-110"
                  aria-label={`${rating} stars`}
                >
                  <Star
                    className={`w-5 h-5 ${
                      contentRating >= rating
                        ? "fill-red-500 text-red-500"
                        : "fill-gray-100 text-gray-300"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Only show the second question if the first question has been answered */}
          {contentRating > 0 && (
            <div className="space-y-2 pt-1">
              <h3 className="text-sm font-medium text-gray-700">
                Future collaboration likelihood
              </h3>
              <p className="text-xs text-gray-500">
                How likely are you to work with them again? (1 = least likely,
                10 = most likely)
              </p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => setCollaborationLikelihood(rating)}
                    className={`w-7 h-7 rounded-full focus:outline-none transition-transform hover:scale-110 text-xs font-medium flex items-center justify-center
                    ${
                      collaborationLikelihood === rating
                        ? "bg-red-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-red-50"
                    }`}
                  >
                    {rating}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Only show the feedback question if the second question has been answered */}
          {collaborationLikelihood > 0 && (
            <div className="space-y-2 pt-1">
              <h3 className="text-sm font-medium text-gray-700">
                Additional Feedback{" "}
                <span className="text-xs text-gray-500">(optional)</span>
              </h3>
              <p className="text-xs text-gray-500">
                Share anything specific you want us to know
              </p>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Enter your feedback here..."
                className="w-full p-2.5 text-sm border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent"
                rows={3}
              />

              <button
                onClick={handleSubmit}
                className="w-full mt-2 bg-red-600 hover:bg-red-700 text-white py-2 rounded-md font-medium transition-colors flex items-center justify-center"
                disabled={isSubmitting || !collaborationLikelihood}
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  "Submit Feedback"
                )}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="px-4 py-6 flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-red-600 mb-2">
            Thank you for sharing your feedback!
          </h3>

          <p className="text-sm text-gray-600 ">
            Your rating stays private and helps us recommend better-fit
            influencers for your future campaigns.
          </p>
          <div className="flex gap-1.5 mt-2">
            {Array(contentRating)
              .fill(0)
              .map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-red-500 text-red-500" />
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
